import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { LandholdingDraft, ProjectDraft, SelectData } from "../../../../../models";
import { createGroup, createProject, CreateProjectRequest } from "../../../../../service/project";
import { lookUp, searchStandards } from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { RadioChangeEvent } from "../../../../../widget/forms/Input/RadioButtonGroup/components";

type LandholdingChoice = "yes" | "no";

interface UseCreateProjectPanelReturnData {
  landholdingChoice: LandholdingChoice;
  landholdingDraft: LandholdingDraft;
  projectDetails: ProjectDraft[];
  countryOptions: SelectData | undefined;
  getProjectTypeOptions: (standardUuid: string) => SelectData;
  errors: ServiceError[];
  isHandleSaveLoading: boolean;
  getStandardOptions: (projectTypeUuid: string) => SelectData;
  updateLandholdingChoice: (event: RadioChangeEvent) => void;
  updateLandholdingField: <K extends keyof LandholdingDraft>(field: K) => (value: LandholdingDraft[K]) => void;
  updateProjectField: <K extends keyof ProjectDraft>(id: string, field: K, value: ProjectDraft[K]) => void;
  updateProjectTypeField: (id: string, value: string) => void;
  updateStandardField: (id: string, value: string) => void;
  addProjectDetails: () => void;
  removeProjectDetails: (id: string) => void;
  handleSave: () => void;
}

const createProjectDraft = (): ProjectDraft => ({
  key: uuidv4(), // Unique ref for landholding + react
  name: "",
  projectTypeUuid: "",
  standardUuid: "",
  developerUuid: "",
  region: "",
  postcode: "",
  latitude: "",
  longitude: "",
  countryCode: "",
  areaNetHa: "",
});

const createLandholdingDraft = (): LandholdingDraft => ({
  key: uuidv4(), // Unique ref for landholding + react
  name: "",
  reference: "",
  referenceId: "",
  region: "",
  latitude: "",
  longitude: "",
  countryCode: "",
  projectKeys: [],
});

const formatCreateProjectErrors = (projectIndex: number, serviceErrors: ServiceError[] | undefined): ServiceError[] =>
  (serviceErrors ?? []).map((error) => {
    let { message } = error;
    message = getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property });
    if (error.code === "VALIDATION_ATTRIBUTE_REQUIRED") {
      switch (error.property) {
        case "StandardUuid":
          return { ...error, message: `Project ${projectIndex + 1}: Standard is a required field` };
        case "ProjectTypeUuid":
          return { ...error, message: `Project ${projectIndex + 1}: Project type is a required field` };
        case "DisplayName":
          return { ...error, message: `Project ${projectIndex + 1}: Project name is a required field` };
        default:
          break;
      }
    }
    return {
      ...error,
      message: `Project ${projectIndex + 1}: ${message}`,
    };
  });

const formatCreateGroupErrors = (serviceErrors: ServiceError[] | undefined): ServiceError[] =>
  (serviceErrors ?? []).map((error) => {
    let { message } = error;
    message = getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property });
    return { ...error, message };
  });

const buildCreateProjectRequest = (project: ProjectDraft): Omit<CreateProjectRequest, "validateOnly"> => ({
  currentDeveloperUuid: null,
  displayName: project.name,
  projectTypeUuid: project.projectTypeUuid,
  standardUuid: project.standardUuid,
  addressCountryCode: project.countryCode || null,
  addressPostcode: project.postcode || null,
  addressRegion: project.region || null,
  areaNetHa: project.areaNetHa ? Number(project.areaNetHa) : null,
  locationCoordinates:
    project.latitude || project.longitude ? `${project.latitude || ""},${project.longitude || ""}` : null,
});

export const useCreateProjectPanel = (
  onSave: (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => void,
  onClose: () => void
): UseCreateProjectPanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [landholdingChoice, setLandholdingChoice] = useState<LandholdingChoice>("no");
  const [isHandleSaveLoading, setIsHandleSaveLoading] = useState(false);
  const [projectDetails, setProjectDetails] = useState<ProjectDraft[]>([createProjectDraft()]);
  const [landholdingDraft, setLandholdingDraft] = useState<LandholdingDraft>(createLandholdingDraft());
  const [countryOptions, setCountryOptions] = useState<SelectData | undefined>();
  const [standardOptions, setStandardOptions] = useState<SelectData>([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState<SelectData>([]);
  const [standardOptionsByProjectType, setStandardOptionsByProjectType] = useState<Record<string, SelectData>>({});
  const [projectTypeOptionsByStandard, setProjectTypeOptionsByStandard] = useState<Record<string, SelectData>>({});

  const updateLandholdingChoice = useCallback((event: RadioChangeEvent) => {
    const value = event.target.value as LandholdingChoice;
    setLandholdingChoice(value);
    if (value === "no") {
      setProjectDetails([createProjectDraft()]);
      setLandholdingDraft(createLandholdingDraft());
    }
  }, []);

  const updateLandholdingField = useCallback(
    <K extends keyof LandholdingDraft>(field: K) =>
      (value: LandholdingDraft[K]) => {
        setLandholdingDraft((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const updateProjectField = useCallback(
    <K extends keyof ProjectDraft>(key: string, field: K, value: ProjectDraft[K]) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => (project.key === key ? { ...project, [field]: value } : project))
      );
    },
    [setProjectDetails]
  );

  const updateProjectTypeField = useCallback(
    (key: string, value: string) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => {
          if (project.key !== key) {
            return project;
          }

          const allowedStandards = standardOptionsByProjectType[value] ?? [];
          const isCurrentStandardAllowed =
            !value ||
            !project.standardUuid ||
            allowedStandards.some((standard) => standard.key === project.standardUuid);

          return {
            ...project,
            projectTypeUuid: value,
            standardUuid: isCurrentStandardAllowed ? project.standardUuid : "",
          };
        })
      );
    },
    [standardOptionsByProjectType]
  );

  const updateStandardField = useCallback(
    (key: string, value: string) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => {
          if (project.key !== key) {
            return project;
          }

          const allowedProjectTypes = projectTypeOptionsByStandard[value] ?? [];
          const isCurrentProjectTypeAllowed =
            !value ||
            !project.projectTypeUuid ||
            allowedProjectTypes.some((projectType) => projectType.key === project.projectTypeUuid);

          return {
            ...project,
            standardUuid: value,
            projectTypeUuid: isCurrentProjectTypeAllowed ? project.projectTypeUuid : "",
          };
        })
      );
    },
    [projectTypeOptionsByStandard]
  );

  const getStandardOptions = useCallback(
    (projectTypeUuid: string): SelectData => {
      if (!projectTypeUuid) {
        return standardOptions;
      }

      return standardOptionsByProjectType[projectTypeUuid] ?? [];
    },
    [standardOptions, standardOptionsByProjectType]
  );

  const getProjectTypeOptions = useCallback(
    (standardUuid: string): SelectData => {
      if (!standardUuid) {
        return projectTypeOptions;
      }

      return projectTypeOptionsByStandard[standardUuid] ?? [];
    },
    [projectTypeOptions, projectTypeOptionsByStandard]
  );

  const addProjectDetails = useCallback(() => {
    setErrors([]);
    setProjectDetails((prev) => [...prev, createProjectDraft()]);
  }, []);

  const removeProjectDetails = useCallback((key: string) => {
    setErrors([]);
    setProjectDetails((prev) => prev.filter((project) => project.key !== key));
  }, []);

  const handleSave = useCallback(async () => {
    setErrors([]);
    setIsHandleSaveLoading(true);

    if (landholdingChoice === "yes") {
      for (let projectIndex = 0; projectIndex < projectDetails.length; projectIndex += 1) {
        // eslint-disable-next-line no-await-in-loop
        const validationResponse = await createProject({
          ...buildCreateProjectRequest(projectDetails[projectIndex]),
          validateOnly: true,
        });

        if (validationResponse.status === Status.Error) {
          setErrors(formatCreateProjectErrors(projectIndex, validationResponse.errors));
          setIsHandleSaveLoading(false);
          return;
        }
      }

      const response = await createGroup({
        name: landholdingDraft.name,
        externalReference: landholdingDraft.referenceId,
        externalParty: landholdingDraft.reference,
        addressRegion: landholdingDraft.region || null,
        addressCountryCode: landholdingDraft.countryCode || null,
        locationCoordinates:
          landholdingDraft.latitude || landholdingDraft.longitude
            ? `${landholdingDraft.latitude || ""},${landholdingDraft.longitude || ""}`
            : null,
        type: "Landholding",
        projects: projectDetails.map((p) => buildCreateProjectRequest(p)),
      });

      if (response.status === Status.Error) {
        setErrors(formatCreateGroupErrors(response.errors));
        setIsHandleSaveLoading(false);
        return;
      }

      const savedLandholdingDraft = {
        ...landholdingDraft,
        key: response.data?.uuid ?? landholdingDraft.key,
        projectKeys: projectDetails.map((p) => p.key),
      };

      onSave(projectDetails, savedLandholdingDraft, true);
    } else {
      const savedProjects: ProjectDraft[] = [];

      for (let projectIndex = 0; projectIndex < projectDetails.length; projectIndex += 1) {
        const project = projectDetails[projectIndex];
        // eslint-disable-next-line no-await-in-loop
        const response = await createProject({
          ...buildCreateProjectRequest(project),
          validateOnly: false,
        });

        if (response.status === Status.Error) {
          if (savedProjects.length > 0) {
            setProjectDetails(projectDetails.slice(projectIndex));
          }

          setErrors(formatCreateProjectErrors(projectIndex, response.errors));
          setIsHandleSaveLoading(false);
          return;
        }

        if (response.data?.projectUuid) {
          savedProjects.push({ ...project, key: response.data.projectUuid, projectUuid: response.data.projectUuid });
        }
      }

      if (savedProjects.length === 0) {
        setIsHandleSaveLoading(false);
        return;
      }

      onSave(savedProjects, landholdingDraft, false);
    }

    setLandholdingChoice("no");
    setProjectDetails([createProjectDraft()]);
    setLandholdingDraft(createLandholdingDraft());
    onClose();
    setIsHandleSaveLoading(false);
  }, [landholdingChoice, landholdingDraft, onClose, onSave, projectDetails]);

  const fetchData = useCallback(async (): Promise<void> => {
    const countriesRes = await lookUp({ type: "Country", sort: [{ key: "valueString", direction: "asc" }] });

    if (countriesRes.status === Status.Success && countriesRes.data) {
      const filteredCountries = countriesRes.data.filter((country) => country.key.includes("GB-"));

      setCountryOptions(
        filteredCountries.map((country) => ({
          key: country.key,
          value: country.valueString,
        }))
      );
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchStandards = async (): Promise<void> => {
      const response = await searchStandards({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 50,
        },
        filter: {
          results: {
            canCreateNewProjects: {
              operator: "eq",
              value: true,
            },
          },
        },
        sort: [{ key: "results.displayName", direction: "asc" }],
      });

      if (response.status !== Status.Success || !response.data?.results) {
        setStandardOptions([]);
        setProjectTypeOptions([]);
        setStandardOptionsByProjectType({});
        setProjectTypeOptionsByStandard({});
        return;
      }

      const standards = response.data.results
        .filter((standard) => standard.displayName)
        .map((standard) => ({
          key: standard.uuid,
          value: standard.displayName as string,
        }));

      const projectTypes = response.data.results
        .flatMap((standard) => standard.projectTypes ?? [])
        .filter((projectType) => projectType.displayName)
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .reduce((accumulator, projectType) => {
          if (!accumulator.some((entry) => entry.key === projectType.uuid)) {
            accumulator.push({
              key: projectType.uuid,
              value: projectType.displayName,
            });
          }
          return accumulator;
        }, [] as SelectData);

      const nextStandardOptionsByProjectType = response.data.results.reduce<Record<string, SelectData>>(
        (accumulator, standard) => {
          if (!standard.displayName) {
            return accumulator;
          }

          standard.projectTypes?.forEach((projectType) => {
            if (!accumulator[projectType.uuid]) {
              accumulator[projectType.uuid] = [];
            }

            if (!accumulator[projectType.uuid].some((entry) => entry.key === standard.uuid)) {
              accumulator[projectType.uuid].push({
                key: standard.uuid,
                value: standard.displayName,
              });
            }
          });

          return accumulator;
        },
        {}
      );

      const nextProjectTypeOptionsByStandard = response.data.results.reduce<Record<string, SelectData>>(
        (accumulator, standard) => {
          if (!standard.displayName) {
            return accumulator;
          }

          accumulator[standard.uuid] = (standard.projectTypes ?? [])
            .filter((projectType) => projectType.displayName)
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map((projectType) => ({
              key: projectType.uuid,
              value: projectType.displayName,
            }));

          return accumulator;
        },
        {}
      );

      setStandardOptions(standards);
      setProjectTypeOptions(projectTypes);
      setStandardOptionsByProjectType(nextStandardOptionsByProjectType);
      setProjectTypeOptionsByStandard(nextProjectTypeOptionsByStandard);
    };

    fetchStandards();
  }, []);

  return {
    landholdingChoice,
    landholdingDraft,
    projectDetails,
    countryOptions,
    getProjectTypeOptions,
    errors,
    isHandleSaveLoading,
    getStandardOptions,
    updateLandholdingChoice,
    updateLandholdingField,
    updateProjectField,
    updateProjectTypeField,
    updateStandardField,
    addProjectDetails,
    removeProjectDetails,
    handleSave,
  };
};
